var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { Controller } from "@hotwired/stimulus";
const ACTIVE_UPLOADS_SELECTOR = "[data-active-uploads]";
const ATTACHMENT_SELECTOR = ".attachment-details[data-filename]";
const DROPZONE_SELECTOR = "[data-dropzone]";
const DROPZONE_ITEMS_SELECTOR = "[data-dropzone-items]";
const DROPZONE_ITEM_SELECTOR = "li[data-filename]";
const DROPZONE_PLACEHOLDER_SELECTOR = "[data-dropzone-no-items]";
const AT_LIMIT_CLASS = "pt-files-at-limit";
const CORE_DISABLED_CLASS = "is-disabled";
const COUNTER_CLASS = "pt-files-counter";
const REJECTED_CLASS = "pt-files-rejected";
export default class files_limit_controller_default extends Controller {
  connect() {
    this.limitEnabled = this.hasMaxValue && this.maxValue > 0;
    this.extensionsEnabled = this.hasAcceptedExtensionsValue && this.acceptedExtensionsValue.length > 0;
    if (!this.limitEnabled && !this.extensionsEnabled) return;
    this.dropZone = this.element.querySelector(DROPZONE_SELECTOR);
    this.dropzoneItems = this.element.querySelector(DROPZONE_ITEMS_SELECTOR);
    this.activeUploads = this.element.querySelector(ACTIVE_UPLOADS_SELECTOR);
    this.placeholder = this.element.querySelector(DROPZONE_PLACEHOLDER_SELECTOR);
    this.input = this.dropZone && this.dropZone.querySelector("input[type=file]");
    this.label = this.placeholder && this.placeholder.querySelector("label");
    this.modal = this.dropZone && this.dropZone.closest(".upload-modal");
    this.modalPopulated = false;
    this.buildNotices();
    this.applyAcceptAttribute();
    this.boundOnChange = this.onInputChange.bind(this);
    this.boundOnDrop = this.onDrop.bind(this);
    document.addEventListener("change", this.boundOnChange, true);
    document.addEventListener("drop", this.boundOnDrop, true);
    if (!this.limitEnabled) return;
    if (this.dropzoneItems) {
      this.observer = new MutationObserver(() => this.refreshLimitState());
      this.observer.observe(this.dropzoneItems, { childList: true });
    }
    this.attributeObserver = new MutationObserver(() => this.refreshLimitState());
    if (this.placeholder) this.attributeObserver.observe(this.placeholder, { attributes: true, attributeFilter: ["class"] });
    if (this.input) this.attributeObserver.observe(this.input, { attributes: true, attributeFilter: ["disabled"] });
    this.boundOnClick = () => requestAnimationFrame(() => this.refreshLimitState());
    this.element.addEventListener("click", this.boundOnClick);
    this.refreshLimitState();
  }
  disconnect() {
    if (this.boundOnChange) document.removeEventListener("change", this.boundOnChange, true);
    if (this.boundOnDrop) document.removeEventListener("drop", this.boundOnDrop, true);
    if (this.boundOnClick) this.element.removeEventListener("click", this.boundOnClick);
    if (this.observer) this.observer.disconnect();
    if (this.attributeObserver) this.attributeObserver.disconnect();
  }
  // The notices go before the items list so the placeholder stays the dropzone
  // container's `:last-child`, which core CSS relies on for its padding.
  buildNotices() {
    if (!this.dropZone || !this.dropzoneItems) return;
    if (this.limitEnabled) {
      this.counter = document.createElement("p");
      this.counter.className = COUNTER_CLASS;
      this.counter.setAttribute("data-files-limit-counter", "");
      this.counter.setAttribute("aria-live", "polite");
      this.dropZone.insertBefore(this.counter, this.dropzoneItems);
    }
    if (this.extensionsEnabled) {
      this.rejectedNotice = document.createElement("p");
      this.rejectedNotice.className = REJECTED_CLASS;
      this.rejectedNotice.setAttribute("data-files-rejected", "");
      this.rejectedNotice.setAttribute("role", "alert");
      this.rejectedNotice.hidden = true;
      this.dropZone.insertBefore(this.rejectedNotice, this.dropzoneItems);
    }
  }
  // Filters the operating system's file picker. Purely a convenience: a
  // participant can still pick "all files", and drag-and-drop ignores it, which
  // is why the change/drop handlers below do the real work.
  applyAcceptAttribute() {
    if (!this.extensionsEnabled || !this.input) return;
    this.input.setAttribute("accept", this.acceptedExtensionsValue.map((ext) => `.${ext}`).join(","));
  }
  onInputChange(event) {
    if (event.target !== this.input) return;
    if (this.suppressNextChange) {
      this.suppressNextChange = false;
      return;
    }
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const { accepted, rejected } = this.screen(Array.from(files));
    this.reportRejected(rejected);
    if (accepted.length === files.length) return;
    event.target.files = this.makeFileList(accepted);
  }
  onDrop(event) {
    if (!this.dropZone || !this.dropZone.contains(event.target)) return;
    const files = event.dataTransfer && event.dataTransfer.files;
    if (!files || files.length === 0) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const { accepted, rejected } = this.screen(Array.from(files));
    this.reportRejected(rejected);
    if (accepted.length === 0) return;
    this.input.files = this.makeFileList(accepted);
    this.suppressNextChange = true;
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  }
  // Extension first, then the count: a file rejected for its format should not
  // eat one of the remaining slots.
  screen(files) {
    const rejected = [];
    let accepted = files;
    if (this.extensionsEnabled) {
      accepted = [];
      files.forEach((file) => {
        if (this.acceptedExtensionsValue.includes(this.extensionOf(file.name))) {
          accepted.push(file);
        } else {
          rejected.push(file.name);
        }
      });
    }
    if (this.limitEnabled) accepted = accepted.slice(0, this.remaining());
    return { accepted, rejected };
  }
  extensionOf(filename) {
    const dot = filename.lastIndexOf(".");
    return dot === -1 ? "" : filename.slice(dot + 1).toLowerCase();
  }
  reportRejected(rejected) {
    if (!this.rejectedNotice) return;
    if (rejected.length === 0) {
      this.rejectedNotice.hidden = true;
      this.rejectedNotice.textContent = "";
      return;
    }
    this.rejectedNotice.textContent = this.rejectedExtensionMessageValue.replace("%{files}", rejected.join(", ")).replace("%{extensions}", this.acceptedExtensionsValue.join(", "));
    this.rejectedNotice.hidden = false;
  }
  refreshLimitState() {
    if (!this.limitEnabled) return;
    const used = this.usedCount();
    const atLimit = used >= this.maxValue;
    this.toggleClass(this.modal, AT_LIMIT_CLASS, atLimit);
    this.updateCounter(used, atLimit);
    if (this.input && this.input.disabled !== atLimit) this.input.disabled = atLimit;
    this.toggleClass(this.placeholder, CORE_DISABLED_CLASS, atLimit);
    if (this.label) {
      this.setAttr(this.label, "disabled", atLimit ? "disabled" : null);
      this.setAttr(this.label, "aria-disabled", String(atLimit));
      this.setAttr(this.label, "title", atLimit ? this.atLimitMessage() : null);
    }
  }
  // Guarded writes: re-asserting an unchanged value must not emit a mutation
  // record, or the attribute observer above would feed itself forever.
  toggleClass(element, className, on) {
    if (!element) return;
    if (element.classList.contains(className) !== on) element.classList.toggle(className, on);
  }
  setAttr(element, name, value) {
    if (!element) return;
    if (value === null) {
      if (element.hasAttribute(name)) element.removeAttribute(name);
    } else if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
  }
  updateCounter(used, atLimit) {
    if (!this.counter) return;
    let text = this.counterLabelValue.replace("%{count}", used).replace("%{max}", this.maxValue);
    if (atLimit && this.hasAtLimitLabelValue) text += ` \u2014 ${this.atLimitLabelValue}`;
    if (this.counter.textContent !== text) this.counter.textContent = text;
    this.toggleClass(this.counter, "is-at-limit", atLimit);
  }
  atLimitMessage() {
    return this.hasAtLimitMessageValue ? this.atLimitMessageValue.replace("%{limit}", this.maxValue) : "";
  }
  // While the modal is open its items list mirrors the saved attachments
  // (`preloadFiles` copies them in), so it is the source of truth. The
  // `data-active-uploads` fallback only covers the window before that first
  // copy: once the list has held anything, an empty list means "the participant
  // removed them", not "we do not know yet".
  usedCount() {
    const inModal = this.dropzoneItems ? this.dropzoneItems.querySelectorAll(DROPZONE_ITEM_SELECTOR).length : 0;
    if (inModal > 0) {
      this.modalPopulated = true;
      return inModal;
    }
    if (this.modalPopulated) return 0;
    return this.activeUploads ? this.activeUploads.querySelectorAll(ATTACHMENT_SELECTOR).length : 0;
  }
  remaining() {
    return Math.max(0, this.maxValue - this.usedCount());
  }
  makeFileList(files) {
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    return dt.files;
  }
}
__publicField(files_limit_controller_default, "values", {
  max: Number,
  acceptedExtensions: Array,
  counterLabel: String,
  atLimitLabel: String,
  atLimitMessage: String,
  rejectedExtensionMessage: String
});
